import React, { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { api, imgUrl } from "../../lib/api";
import { toast } from "sonner";

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export default function ImageUploader({ value = [], onChange, max = 6, testid = "image-uploader" }) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const onSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (value.length + files.length > max) {
      toast.error(`Max ${max} images`);
      return;
    }
    for (const f of files) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        toast.error(`${f.name}: only JPG/PNG/WebP/GIF allowed`);
        return;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: exceeds 10MB`);
        return;
      }
    }
    setLoading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.key || data.url || data.storage_path);
      }
      onChange([...value, ...uploaded]);
      toast.success(`Uploaded ${uploaded.length} image(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
        {value.map((src, i) => (
          <div key={i} className="relative aspect-[3/4] border border-[hsl(var(--kb-border))] overflow-hidden">
            <img src={imgUrl(src)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-1 right-1 bg-white p-1 shadow"
              data-testid={`${testid}-remove-${i}`}
            >
              <X size={12} />
            </button>
          </div>
        ))}
        {value.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="aspect-[3/4] border border-dashed border-[hsl(var(--kb-border))] flex flex-col items-center justify-center gap-2 text-xs uppercase tracking-wider text-[hsl(var(--kb-muted))] hover:bg-[hsl(var(--kb-surface-alt))]"
            data-testid={`${testid}-add`}
            disabled={loading}
          >
            <Upload size={16} />
            {loading ? "Uploading…" : "Add image"}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onSelect}
      />
    </div>
  );
}
